import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authService, LoginRequest } from '../lib/api/auth'

interface User {
    id: string
    email: string
    name?: string
    avatarUrl?: string
    role: string
    isActive: boolean
    createdAt: string
}

interface Tokens {
    accessToken: string
    refreshToken: string
    expiresIn: number
}

interface AuthState {
    user: User | null;
    tokens: Tokens | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    login: (credentials: LoginRequest) => Promise<void>;
    logout: () => void;
    setTokens: (tokens: Tokens) => void;
    setUser: (user: User) => void;
    updateUser: (user: Partial<User>) => void;
    refreshTokens: () => Promise<Tokens>;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            tokens: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,

            login: async (credentials: LoginRequest) => {
                set({ isLoading: true, error: null })
                try {
                    const response = await authService.login(credentials)
                    if (response.success) {
                        const { user, tokens } = response.data
                        set({
                            user: user,
                            tokens: {
                                accessToken: tokens.accessToken,
                                refreshToken: tokens.refreshToken,
                                expiresIn: tokens.expiresIn || 3600
                            },
                            isAuthenticated: true,
                            isLoading: false,
                            error: null
                        })
                    }
                } catch (error: any) {
                    console.error('Login failed details (response data):', error.response?.data);
                    console.error('Login error object:', error);
                    // Use friendly error messages
                    let friendlyError = 'Unable to sign in. Please try again.';
                    const status = error.response?.status;
                    const serverError = error.response?.data?.error;

                    if (status === 401 || serverError?.includes('Invalid') || serverError?.includes('credentials')) {
                        friendlyError = 'Invalid email or password. Please check your credentials.';
                    } else if (status === 403) {
                        friendlyError = 'Access denied. You don\'t have permission to access this area.';
                    } else if (status >= 500 || !error.response) {
                        friendlyError = 'We\'re experiencing technical difficulties. Please try again later.';
                    }

                    set({
                        isLoading: false,
                        error: friendlyError
                    })
                    throw error
                }
            },

            logout: async () => {
                set({ isLoading: true })
                try {
                    await authService.logout()
                } catch (error) {
                    // ignore
                } finally {
                    set({
                        user: null,
                        tokens: null,
                        isAuthenticated: false,
                        isLoading: false,
                        error: null
                    })
                }
            },

            setTokens: (tokens: Tokens) => set({ tokens }),
            setUser: (user: User) => set({ user }),
            updateUser: (updatedUser: Partial<User>) => set((state) => ({
                user: state.user ? { ...state.user, ...updatedUser } : null
            })),

            refreshTokens: async (): Promise<Tokens> => {
                const tokens = (get() as AuthState).tokens;
                if (!tokens?.refreshToken) {
                    throw new Error('No refresh token available');
                }

                try {
                    const response = await authService.refreshToken(tokens.refreshToken);
                    if (response.data.success) {
                        const newTokens = response.data.data.tokens;
                        const tokensObj = {
                            accessToken: newTokens.accessToken,
                            refreshToken: newTokens.refreshToken,
                            expiresIn: newTokens.expiresIn || 3600
                        };
                        set({ tokens: tokensObj });
                        return tokensObj;
                    }
                    throw new Error('Refresh failed');
                } catch (error) {
                    set({
                        user: null,
                        tokens: null,
                        isAuthenticated: false,
                        error: 'Session expired. Please log in again.'
                    });
                    throw error;
                }
            },
        }),
        {
            name: 'admin-auth-storage',
            partialize: (state) => ({
                user: state.user,
                tokens: state.tokens,
                isAuthenticated: state.isAuthenticated,
            }),
        }
    )
)
